import type { WidgetLoginResponse } from '@teo/widget-shared';
import type { ScriptTagConfig } from '../config/parse-script-config.js';

import { clearStoredWidgetAuth, patchStoredWidgetAuthThreadId } from './session-storage.js';

export type WidgetSession = {
  sessionId: number;
  companyId: number;
  moduleType: number;
  token: string;
  /** Visitor fingerprint — used as `user_id` in `POST /api/widget/send` */
  visitorId: string;
  /** `null` until backend assigns a thread */
  threadId: number | null;
  slug: string;
};

let activeSession: WidgetSession | null = null;

export function createWidgetSession(
  login: Pick<
    WidgetLoginResponse,
    'id' | 'company_id' | 'module_type' | 'token' | 'thread_id'
  >,
  config: ScriptTagConfig,
  visitorId: string,
): WidgetSession {
  if (!config.slug) {
    throw new Error('[Teo Widget] slug is required for session');
  }

  return {
    sessionId: login.id,
    companyId: login.company_id,
    moduleType: login.module_type,
    token: login.token,
    visitorId,
    threadId: login.thread_id,
    slug: config.slug,
  };
}

export function setWidgetSession(session: WidgetSession): void {
  activeSession = session;
}

export function getWidgetSession(): WidgetSession | null {
  return activeSession;
}

export function clearWidgetSession(slug?: string): void {
  const slugToClear = slug ?? activeSession?.slug;
  activeSession = null;
  if (slugToClear) {
    clearStoredWidgetAuth(slugToClear);
  }
}

/** Called after first message when backend returns `thread_id`. */
export function updateWidgetSessionThreadId(threadId: number): void {
  if (!activeSession) return;
  activeSession = { ...activeSession, threadId };
  patchStoredWidgetAuthThreadId(activeSession.slug, threadId);
}

/** Laravel Sanctum-style bearer token for authenticated widget API calls. */
export function getWidgetAuthHeaders(): Record<string, string> {
  const token = activeSession?.token;
  if (!token) return {};

  return {
    Authorization: `Bearer ${token}`,
  };
}
